/**
 * Auth API client: calls BFF (ezy-bff) at PRD_BFF_URL.
 * The BFF adds the backend API key server-side; the frontend does not send one.
 */

import { getAuthConfig } from "@/config/auth";
import type { Ad } from "@/app/components/ad-card";
import { enrichAdCategoryFromCatalog, getAdImage } from "@/app/utils/ad-utils";
import { isOpaqueCatalogCategoryId } from "@/app/utils/category-tree";
import { compressProfilePhotoForUpload } from "@/app/utils/compress-image-to-max-bytes";

const STORAGE_ACCESS = "ezyhub_access_token";
const STORAGE_REFRESH = "ezyhub_refresh_token";

/** Called when a 401 occurs and refresh failed or no refresh token; client should clear session UI. */
let onSessionExpired: (() => void) | null = null;

export function setOnSessionExpired(callback: (() => void) | null): void {
  onSessionExpired = callback;
}

/** Single in-flight refresh so multiple 401s don't each trigger a refresh (avoids race and extra 401s). */
let refreshPromise: Promise<void> | null = null;

function getOrStartRefresh(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      await refresh();
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface SignupResponse {
  message: string;
  user_id: string;
}

export interface VerifyEmailResponse {
  message: string;
  user_id?: string;
}

export interface MeResponse {
  user_id: string;
  email: string;
  email_verified: boolean;
  name: string;
  /** True when the account has an email/password credential. Omitted by older APIs; treat as true if missing. */
  has_password?: boolean;
  /** Linked Google OAuth (google_id on user). Omitted by older APIs. */
  google_linked?: boolean;
  /** Linked Apple OAuth (apple_id on user). Omitted by older APIs. */
  apple_linked?: boolean;
}

/** Customer profile (GET /auth/me/profile). Matches backend document shape. */
export interface ProfilePersonal {
  cpf: string;
  cpf_verified: boolean;
  birth_date: string;
  gender?: string;
  preferred_name?: string;
}

export interface ProfileContact {
  phone: string;
  phone_verified: boolean;
  email_verified: boolean;
}

export interface ProfileAddress {
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  is_default: boolean;
}

export interface Profile {
  customer_id: string;
  user_id: string;
  personal: ProfilePersonal;
  contact: ProfileContact;
  addresses: ProfileAddress[];
  profile_image_url?: string;
  onboarding_completed_at: string | null;
  created_at: string;
  updated_at: string;
  /** Stripe Connect account ID for seller payouts (Conta Digital). */
  stripe_connected_account_id?: string;
}

/** Partial profile for PATCH /auth/me/profile. Only sent fields are applied. */
export interface ProfilePatch {
  name?: string;
  personal?: ProfilePersonal;
  contact?: ProfileContact;
  addresses?: ProfileAddress[];
  profile_image_url?: string;
  onboarding_completed_at?: string | null;
}

export interface AuthApiError {
  error: string;
  message?: string;
  fields?: Record<string, string>;
}

function getHeaders(includeAuth = false): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (includeAuth) {
    const access = getAccessToken();
    if (access) headers["Authorization"] = `Bearer ${access}`;
  }
  return headers;
}

function getBaseUrl(): string {
  const { baseUrl } = getAuthConfig();
  if (!baseUrl) throw new Error("PRD_BFF_URL is not set");
  return baseUrl;
}

/** BFF rate limit returns integer seconds; cap wait to avoid long UI stalls. */
function parseRetryAfterSeconds(retryAfter: string | null): number {
  if (retryAfter == null || retryAfter.trim() === "") return 1;
  const n = Number.parseInt(retryAfter.trim(), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, 10);
}

export function getBffBaseUrl(): string {
  return getBaseUrl();
}

/** Performs a single fetch; no retry. Used by request() for 401 refresh and optional 429 backoff. */
async function doOneRequest<T>(
  path: string,
  init: RequestInit,
  useBearer: boolean
): Promise<{ body: T; status: number; retryAfterSec?: number }> {
  const url = `${getBaseUrl()}${path}`;
  const res = await fetch(url, {
    ...init,
    credentials: "include",
    headers: { ...getHeaders(useBearer), ...(init.headers as Record<string, string>) },
  });
  const body = await res.json().catch(() => ({})) as T;
  const retryAfterSec = res.status === 429 ? parseRetryAfterSeconds(res.headers.get("Retry-After")) : undefined;
  return { body: body as T, status: res.status, retryAfterSec };
}

type AuthRequestInit = RequestInit & { skipAuth?: boolean; max429Retries?: number };

async function request<T>(path: string, options: AuthRequestInit = {}): Promise<T> {
  const { skipAuth, max429Retries: raw429 = 0, ...init } = options;
  const max429Retries = Math.max(0, raw429);
  const useBearer = !skipAuth && !!getAccessToken();
  const maxAttempts = max429Retries + 1;

  const handleError = (status: number, body: unknown): never => {
    const err: AuthApiError = {
      error: (body as AuthApiError)?.error ?? "request_failed",
      message: (body as AuthApiError)?.message,
      fields: (body as AuthApiError)?.fields,
    };
    if (!(path === "/auth/me/profile" && status === 404)) {
      console.warn("[auth] request failed:", path, "status:", status, "error:", err.error, "message:", err.message ?? "");
    }
    const displayMessage =
      typeof err.message === "string" && err.message.trim() !== "" ? err.message.trim() : err.error;
    const listingIssues =
      err.error === "listing_quality_insufficient" &&
      Array.isArray((body as { issues?: unknown }).issues)
        ? ((body as { issues: Array<{ message?: string }> }).issues ?? [])
        : undefined;
    throw Object.assign(new Error(displayMessage), {
      status,
      ...err,
      ...(listingIssues ? { issues: listingIssues } : {}),
    }) as Error & AuthApiError & { status: number };
  };

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let result = await doOneRequest<unknown>(path, init, useBearer);

    if (result.status === 401 && useBearer && getRefreshToken()) {
      try {
        await getOrStartRefresh();
        result = await doOneRequest<unknown>(path, init, true);
      } catch {
        refreshPromise = null;
        clearTokens();
        onSessionExpired?.();
        throw Object.assign(new Error("missing or invalid token"), { status: 401 });
      }
      if (result.status === 401) {
        clearTokens();
        onSessionExpired?.();
        throw Object.assign(new Error("missing or invalid token"), { status: 401 });
      }
    }

    if (result.status === 429 && attempt < max429Retries) {
      const sec = result.retryAfterSec ?? 1;
      await new Promise((r) => setTimeout(r, sec * 1000));
      continue;
    }

    if (result.status < 200 || result.status >= 300) {
      handleError(result.status, result.body);
    }
    return result.body as T;
  }

  throw Object.assign(new Error("too many requests"), {
    status: 429,
    error: "rate_limited",
  } as AuthApiError);
}

/** Get stored access token (memory or localStorage). */
export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_ACCESS);
}

/** Get stored refresh token. */
export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_REFRESH);
}

/** Store tokens after login/refresh. */
export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(STORAGE_ACCESS, access);
  localStorage.setItem(STORAGE_REFRESH, refresh);
}

/** Clear tokens (logout). */
export function clearTokens(): void {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
}

/** Check if auth is configured (base URL set). */
export function isAuthConfigured(): boolean {
  try {
    return !!getAuthConfig().baseUrl;
  } catch {
    return false;
  }
}

/**
 * POST /auth/login
 * On success, tokens are stored automatically.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const path = "/auth/login";
  const res = await request<LoginResponse>(path, {
    method: "POST",
    body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    skipAuth: true,
  });
  setTokens(res.access_token, res.refresh_token);
  return res;
}

/**
 * POST /auth/signup
 * Does not log the user in; they must verify email then login.
 */
export async function signup(
  email: string,
  password: string,
  name: string,
  termsAccepted: boolean,
  privacyAccepted: boolean
): Promise<SignupResponse> {
  const path = "/auth/signup";
  return request<SignupResponse>(path, {
    method: "POST",
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      password,
      name: name.trim(),
      terms_accepted: termsAccepted,
      privacy_accepted: privacyAccepted,
    }),
    skipAuth: true,
  });
}

/**
 * POST /auth/verify-email
 * Verifies the user's email with the token from the verification link.
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const path = "/auth/verify-email";
  return request<VerifyEmailResponse>(path, {
    method: "POST",
    body: JSON.stringify({ token: token.trim() }),
    skipAuth: true,
  });
}

export interface ForgotPasswordResponse {
  message: string;
}

/**
 * POST /auth/forgot-password
 * Request a password reset link by email. Backend always returns 200 with a generic message.
 */
export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
  const path = "/auth/forgot-password";
  return request<ForgotPasswordResponse>(path, {
    method: "POST",
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
    skipAuth: true,
  });
}

export interface ResetPasswordResponse {
  message: string;
}

/**
 * POST /auth/reset-password
 * Set a new password using the token from the reset link.
 */
export async function resetPassword(token: string, newPassword: string): Promise<ResetPasswordResponse> {
  const path = "/auth/reset-password";
  return request<ResetPasswordResponse>(path, {
    method: "POST",
    body: JSON.stringify({ token: token.trim(), new_password: newPassword }),
    skipAuth: true,
  });
}

export interface ChangePasswordResponse {
  message: string;
}

/**
 * PATCH /auth/me/password
 * Change password while logged in. Requires Bearer. Returns 400 if account has no password (SSO-only).
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<ChangePasswordResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ChangePasswordResponse>("/auth/me/password", {
    method: "PATCH",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

/** POST /auth/me/account-deletion — reauth + orchestrated erasure (BFF). */
export interface AccountDeletionComponentResult {
  component?: string;
  status?: string;
  detail?: string;
  deleted_count?: number;
  http_status?: number;
}

export interface AccountDeletionResponse {
  user_id: string;
  components: AccountDeletionComponentResult[];
}

export async function requestAccountDeletion(body: {
  password?: string;
  confirmation?: string;
}): Promise<AccountDeletionResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<AccountDeletionResponse>("/auth/me/account-deletion", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface IdentityActionResponse {
  message: string;
}

/**
 * DELETE /auth/me/identities/google|apple — unlink provider. Requires Bearer.
 */
export async function unlinkIdentity(provider: "google" | "apple"): Promise<IdentityActionResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const path =
    provider === "google" ? "/auth/me/identities/google" : "/auth/me/identities/apple";
  return request<IdentityActionResponse>(path, { method: "DELETE" });
}

/**
 * POST /auth/me/identities/google — link Google to current account (id_token must match account email).
 */
export async function linkGoogleIdentity(idToken: string): Promise<IdentityActionResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<IdentityActionResponse>("/auth/me/identities/google", {
    method: "POST",
    body: JSON.stringify({ id_token: idToken.trim() }),
  });
}

/**
 * POST /auth/me/identities/apple — link Apple to current account.
 */
export async function linkAppleIdentity(
  idToken: string,
  email?: string,
  name?: string
): Promise<IdentityActionResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const body: { id_token: string; email?: string; name?: string } = { id_token: idToken.trim() };
  if (email?.trim()) body.email = email.trim();
  if (name?.trim()) body.name = name.trim();
  return request<IdentityActionResponse>("/auth/me/identities/apple", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * POST /auth/refresh
 * Uses stored refresh_token; stores new tokens on success.
 */
export async function refresh(): Promise<LoginResponse> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw Object.assign(new Error("refresh_token is required"), { status: 400 });
  const path = "/auth/refresh";
  const res = await request<LoginResponse>(path, {
    method: "POST",
    body: JSON.stringify({ refresh_token: refreshToken }),
    skipAuth: true,
  });
  setTokens(res.access_token, res.refresh_token);
  return res;
}

/**
 * POST /auth/logout
 * Revokes the stored refresh_token. Call clearTokens() after.
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  const path = "/auth/logout";
  const headers = getHeaders(false);
  const body = refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : "{}";
  try {
    await fetch(`${getBaseUrl()}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { ...headers, "Content-Type": "application/json" },
      body,
    });
  } finally {
    clearTokens();
  }
}

/**
 * POST /auth/google
 * Exchange Google ID token (from Google Identity Services) for access + refresh tokens.
 * On success, tokens are stored automatically.
 */
export async function googleLogin(idToken: string): Promise<LoginResponse> {
  const path = "/auth/google";
  if (!idToken?.trim()) {
    console.warn("[auth] googleLogin: id_token is empty");
    throw Object.assign(new Error("id_token is required"), { status: 400 });
  }
  try {
    const res = await request<LoginResponse>(path, {
      method: "POST",
      body: JSON.stringify({ id_token: idToken.trim() }),
      skipAuth: true,
    });
    setTokens(res.access_token, res.refresh_token);
    return res;
  } catch (e) {
    const err = e as Error & { status?: number; error?: string; message?: string };
    console.warn("[auth] POST /auth/google failed:", "status:", err.status, "error:", err.error, "message:", err.message);
    throw e;
  }
}

/**
 * POST /auth/apple
 * Exchange Apple identity token (from Sign in with Apple) for access + refresh tokens.
 * email and name are optional; Apple sends them only on first authorization.
 * On success, tokens are stored automatically.
 */
export async function appleLogin(
  idToken: string,
  email?: string,
  name?: string
): Promise<LoginResponse> {
  const path = "/auth/apple";
  const body: { id_token: string; email?: string; name?: string } = {
    id_token: idToken.trim(),
  };
  if (email?.trim()) body.email = email.trim();
  if (name?.trim()) body.name = name.trim();
  const res = await request<LoginResponse>(path, {
    method: "POST",
    body: JSON.stringify(body),
    skipAuth: true,
  });
  setTokens(res.access_token, res.refresh_token);
  return res;
}

/**
 * GET /auth/me
 * Requires a valid access token (from login or refresh).
 */
export async function me(): Promise<MeResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<MeResponse>("/auth/me", { method: "GET" });
}

/**
 * GET /auth/me/profile
 * Returns the current user's customer profile. Requires Bearer token.
 * @throws Error with status 404 when the user has no profile yet (e.g. needs onboarding).
 */
export async function getProfile(): Promise<Profile> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<Profile>("/auth/me/profile", { method: "GET" });
}

/**
 * PATCH /auth/me/profile
 * Create or update the customer profile (partial or full). Only provided fields are applied.
 * Requires Bearer token.
 */
export async function saveProfile(payload: ProfilePatch): Promise<Profile> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<Profile>("/auth/me/profile", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// PHASE_1 — Marketing preferences (push + email opt-in/out).

/** Default marketing opt-in shown before preferences load and for new users. */
export const DEFAULT_MARKETING_PREFERENCES: Readonly<
  Pick<MarketingPreferences, "marketing_push_opt_in" | "marketing_email_opt_in">
> = {
  marketing_push_opt_in: true,
  marketing_email_opt_in: true,
} as const;

export interface MarketingPreferences {
  user_id: string;
  marketing_push_opt_in: boolean;
  marketing_email_opt_in: boolean;
  status: string;
  marketing_push_opt_in_updated_at?: string;
  marketing_push_opt_in_source?: string;
  marketing_email_opt_in_updated_at?: string;
  marketing_email_opt_in_source?: string;
}

export interface MarketingPreferencesPatch {
  marketing_push_opt_in?: boolean;
  marketing_email_opt_in?: boolean;
}

/** GET /auth/me/preferences — Bonamassa-backed marketing consent state. */
export async function getMarketingPreferences(): Promise<MarketingPreferences> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<MarketingPreferences>("/auth/me/preferences", { method: "GET" });
}

/** PATCH /auth/me/preferences — flip one or both opt-ins. */
export async function updateMarketingPreferences(
  patch: MarketingPreferencesPatch,
): Promise<MarketingPreferences> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<MarketingPreferences>("/auth/me/preferences", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5MB

/**
 * POST /auth/me/profile/photo
 * Upload a profile photo (multipart). Image is resized (max 512px) and compressed for smaller file and better UI display.
 * Max 5MB after client-side compression (output is JPEG from the compressor). Returns the updated profile with profile_image_url set.
 */
export async function uploadProfilePhoto(file: File): Promise<Profile> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const rawType = (file.type || "").toLowerCase();
  if (rawType && !rawType.startsWith("image/")) {
    throw new Error("Formato inválido. Envie uma imagem.");
  }
  const compressed = await compressProfilePhotoForUpload(file);
  if (!compressed.ok) {
    throw new Error(compressed.error);
  }
  if (compressed.file.size > MAX_PHOTO_BYTES) {
    throw new Error("Arquivo muito grande. Máximo 5MB após compressão.");
  }
  const resized = compressed.file;
  const form = new FormData();
  form.append("photo", resized);
  const url = `${getBaseUrl()}/auth/me/profile/photo`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      typeof (data as { error?: string }).error === "string"
        ? (data as { error: string }).error
        : "Não foi possível enviar a foto.";
    throw Object.assign(new Error(msg), { status: res.status });
  }
  return data as Profile;
}

// --- Ad images (signed upload + signed download) ---

const MAX_AD_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_AD_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"] as const;

export function getMaxAdImageBytes(): number {
  return MAX_AD_IMAGE_BYTES;
}

export function isAllowedAdImageType(mime: string): boolean {
  return ALLOWED_AD_IMAGE_TYPES.includes(mime.toLowerCase() as (typeof ALLOWED_AD_IMAGE_TYPES)[number]);
}

export interface AdUploadUrlResponse {
  upload_url: string;
  storage_path: string;
  max_size_bytes: number;
  allowed_content_types: string[];
}

/**
 * POST /auth/me/ads/{ad_id}/upload-url
 * Get a signed PUT URL to upload one ad image. Requires Bearer token. Caller must be the ad seller.
 */
export async function getAdUploadUrl(
  adId: string,
  contentType: "image/jpeg" | "image/png" = "image/jpeg"
): Promise<AdUploadUrlResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<AdUploadUrlResponse>(`/auth/me/ads/${encodeURIComponent(adId)}/upload-url`, {
    method: "POST",
    body: JSON.stringify({ content_type: contentType }),
  });
}

/** POST /auth/me/listing-drafts/upload-url — signed PUT for temporary AI draft images (no ad_id). */
export async function getListingDraftUploadUrl(
  contentType: "image/jpeg" | "image/png" = "image/jpeg"
): Promise<AdUploadUrlResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<AdUploadUrlResponse>("/auth/me/listing-drafts/upload-url", {
    method: "POST",
    body: JSON.stringify({ content_type: contentType }),
  });
}

/** BFF response after kotzen + catalog resolution (listing draft v2). */
export interface ListingDraftResponse {
  title: string;
  /** v2: 3 alternative titles (different angles). UI shows them as chips. */
  title_options: string[];
  description: string;
  suggested_category_id: string;
  suggested_category_name: string;
  condition: "new" | "used" | "";
  listing_attributes: Record<string, unknown>;
  /** v2: suggested price in BRL cents (0 when AI is unsure). */
  suggested_price_cents: number;
  /** v2: confidence band for the suggested price. */
  price_confidence: "low" | "med" | "high";
  /** v2: 1-sentence rationale shown next to the price ("usado em SP varia R$..."). */
  price_rationale: string;
  shipping_suggestion: Record<string, unknown> | null;
  disclaimer: string;
}

/** Photo coach (§2.2) response. */
export interface PhotoCoachResult {
  scores: {
    brightness: number;
    focus: number;
    background: number;
    framing: number;
    clarity: number;
  };
  overall: number;
  verdict: "great" | "ok" | "needs_work";
  tips: Array<{ id: string; title: string; body: string }>;
  is_product_visible: boolean;
}

/**
 * POST /auth/me/photo-coach — evaluate one uploaded ad photo and return tips.
 */
export async function postPhotoCoach(body: { storage_path: string }): Promise<PhotoCoachResult> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<PhotoCoachResult>("/auth/me/photo-coach", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * POST /auth/me/listing-draft — AI draft from GCS image paths (upload via getListingDraftUploadUrl first).
 * v2: pass an optional `category_id` so the AI fills listing_attributes using the canonical schema.
 */
export async function postListingDraft(body: {
  storage_paths: string[];
  extra_text?: string;
  category_id?: string;
}): Promise<ListingDraftResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ListingDraftResponse>("/auth/me/listing-draft", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * PUT file to the signed upload URL. Content-Type must match what was requested when getting the URL.
 */
export async function uploadFileToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text();
    throw Object.assign(new Error(text || "Upload failed"), { status: res.status });
  }
}

export interface AdSignedUrlResponse {
  url: string;
}

/**
 * GET /auth/me/ads/images/signed-url?path=...
 * Exchange a storage path (opaque key from user_images) for a short-lived signed GET URL.
 * Requires a valid Bearer token; 401 means not logged in, or token expired/invalid (we retry once after refresh).
 */
export async function getAdImageSignedUrl(storagePath: string): Promise<string> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const encoded = encodeURIComponent(storagePath);
  const data = await request<AdSignedUrlResponse>(
    `/auth/me/ads/images/signed-url?path=${encoded}`,
    { method: "GET" }
  );
  return data.url;
}

/**
 * GET /ads/images/signed-url?path=... (public; no Bearer).
 * Same as getAdImageSignedUrl but does not require auth. Use for displaying ad images on feed and ad-detail so anonymous and logged-in users both see images.
 */
export async function getAdImageSignedUrlPublic(storagePath: string): Promise<string> {
  const encoded = encodeURIComponent(storagePath);
  const data = await request<AdSignedUrlResponse>(
    `/ads/images/signed-url?path=${encoded}`,
    { method: "GET", skipAuth: true }
  );
  return data.url;
}

// --- Catalog (categories from product-master via BFF) ---

/** Listing-attribute schema entry from product-master (per category). */
export interface CategoryAttributeDefinition {
  key: string;
  type: string;
  required: boolean;
  label_pt: string;
  options?: string[];
}

/** Category from GET /catalog/categories (BFF proxies to product-master). */
export interface Category {
  category_id: string;
  name: string;
  parent_id?: string;
  slug: string;
  order: number;
  attribute_definitions?: CategoryAttributeDefinition[];
  attribute_schema_version?: number;
}

/** GET /catalog/categories — returns all categories. No auth required. */
export async function getCategories(): Promise<Category[]> {
  const baseUrl = getAuthConfig().baseUrl;
  if (!baseUrl) throw new Error("PRD_BFF_URL is not set");
  const url = `${baseUrl.replace(/\/$/, "")}/catalog/categories`;
  const res = await fetch(url, { credentials: "include", headers: { Accept: "application/json" } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw Object.assign(new Error(err.error ?? "failed to load categories"), { status: res.status });
  }
  const data = (await res.json()) as { categories?: Category[] };
  return data.categories ?? [];
}

/**
 * Expands a list of category_ids to include all ancestors (parent, grandparent, …).
 * Use so banners for parent categories show when any subcategory has ads.
 */
export function expandCategoryIdsWithAncestors(
  categoryIds: string[],
  categories: { category_id: string; parent_id?: string }[]
): string[] {
  const byId = new Map(categories.map((c) => [c.category_id, c]));
  const out = new Set<string>(categoryIds);
  for (const id of categoryIds) {
    let current = byId.get(id);
    while (current?.parent_id) {
      out.add(current.parent_id);
      current = byId.get(current.parent_id);
    }
  }
  return Array.from(out);
}

// --- Ad CRUD (create + patch for images) ---

/** Payload for POST /auth/me/ads. BFF maps to sambora (seller_id from auth, product_id from config, seller_notes = JSON). category = category_id from catalog. */
export interface CreateAdPayload {
  title: string;
  description: string;
  price: number;
  category: string; // category_id from catalog menu
  condition?: "used" | "new";
  accepts_offers?: boolean;
  user_images?: string[];
  /** Optional shipping attributes for freight quote (cm, kg). */
  shipping_weight_kg?: number;
  shipping_width_cm?: number;
  shipping_height_cm?: number;
  shipping_length_cm?: number;
  /** Category-specific listing attributes (BFF exposes Sambora listing_attributes as "attributes"). */
  attributes?: Record<string, unknown>;
}

/** Sambora/BFF ad response. Uses top-level title, description, category_id, accepts_offers; seller_notes kept for future/fallback. BFF may add category_tree when catalog is available. */
export interface ApiAdResponse {
  ad_id: string;
  product_id: string;
  seller_id: string;
  title?: string;
  description?: string;
  category_id?: string;
  /** Full path e.g. "Instrumentos Musicais > Instrumentos de Corda" (added by BFF when catalog available). */
  category_tree?: string;
  accepts_offers?: boolean;
  price: number;
  currency: string;
  condition: string;
  seller_notes?: string;
  user_images?: string[];
  /** Neighborhood, City, ST (from Sambora; BFF passes through). */
  location?: string;
  /** Optional shipping attributes for freight quote (cm, kg). */
  shipping_weight_kg?: number;
  shipping_width_cm?: number;
  shipping_height_cm?: number;
  shipping_length_cm?: number;
  /** Seller CEP for shipping (swap Leg 2.1 destination). */
  shipping_origin_cep?: string;
  status: string;
  created_at: string;
  updated_at: string;
  /** Enriched by BFF from Bonamassa GET /users/:id when loading ad. */
  seller_display_name?: string;
  seller_member_since_year?: number;
  /** Category-specific attributes (BFF maps from Sambora listing_attributes). */
  attributes?: Record<string, unknown>;
  /** When false, BFF marks review seed listings as noindex (default true if omitted). */
  indexing_allowed?: boolean;
  /** Rule-based quality (listing-quality gates). */
  listing_quality_score?: number;
  listing_quality_tier?: string;
  listing_quality_version?: string;
  discovery_eligible?: boolean;
  has_photos?: boolean;
}

export interface ListingQualityIssue {
  code: string;
  message: string;
  points_lost?: number;
  knockout?: boolean;
}

export interface ListingQualityResult {
  score: number;
  tier: string;
  discovery_eligible: boolean;
  has_photos: boolean;
  issues?: ListingQualityIssue[];
  knockouts?: string[];
  min_score_discovery?: number;
  min_score_publish?: number;
}

/** Body for POST /auth/me/listing-quality/preview (draft snapshot). */
export interface ListingQualityPreviewPayload {
  title: string;
  description: string;
  price: number;
  category: string;
  condition?: string;
  user_images?: string[];
  attributes?: Record<string, unknown>;
  shipping_weight_kg?: number;
  shipping_width_cm?: number;
  shipping_height_cm?: number;
  shipping_length_cm?: number;
}

/** Map API ad to frontend Ad. Prefers top-level title, description, category_id/category_tree, accepts_offers; falls back to seller_notes for legacy. */
function categoryFieldsFromApi(api: ApiAdResponse): { category: string; category_tree?: string } {
  const tree = (api.category_tree ?? "").trim();
  const cid = (api.category_id ?? "").trim();
  // Human-readable path from BFF
  if (tree && (!cid || tree !== cid)) {
    return { category: tree, category_tree: tree };
  }
  // BFF echoed id as tree — treat as unresolved
  if (tree && cid && tree === cid && isOpaqueCatalogCategoryId(cid)) {
    return { category: "", category_tree: undefined };
  }
  if (cid && !isOpaqueCatalogCategoryId(cid)) {
    return { category: cid, category_tree: tree || undefined };
  }
  return { category: "", category_tree: tree || undefined };
}

export function apiAdToAd(api: ApiAdResponse, sellerName?: string): Ad {
  let title = api.title ?? "";
  let description = api.description ?? "";
  let { category, category_tree: catTreeField } = categoryFieldsFromApi(api);
  let acceptsOffers = api.accepts_offers ?? true;
  if ((!title || !description || category === "") && api.seller_notes) {
    try {
      const notes = JSON.parse(api.seller_notes) as Record<string, unknown>;
      if (!title) title = (notes.title as string) ?? "";
      if (!description) description = (notes.description as string) ?? "";
      if (category === "") {
        const legCat = (notes.category as string) ?? (notes.category_id as string) ?? "";
        if (legCat && !isOpaqueCatalogCategoryId(legCat)) category = legCat;
      }
      acceptsOffers = (notes.accepts_offers as boolean) ?? acceptsOffers;
    } catch {
      if (!title) title = api.seller_notes;
    }
  }
  if (category === "") category = "Outros";
  let listing_attributes: Record<string, unknown> | undefined;
  if (api.attributes && typeof api.attributes === "object" && !Array.isArray(api.attributes)) {
    listing_attributes = { ...api.attributes };
  }
  return {
    id: api.ad_id,
    title,
    description,
    price: api.price,
    category,
    category_id: api.category_id,
    category_tree: catTreeField,
    listing_attributes,
    location: api.location ?? "",
    image: undefined,
    images: undefined,
    user_images: api.user_images ?? [],
    createdAt: api.created_at,
    seller: api.seller_display_name?.trim() || sellerName || undefined,
    seller_id: api.seller_id,
    sellerMemberSinceYear: api.seller_member_since_year,
    condition: api.condition || "Seminovo",
    acceptsOffers,
    shipping_weight_kg: api.shipping_weight_kg,
    shipping_width_cm: api.shipping_width_cm,
    shipping_height_cm: api.shipping_height_cm,
    shipping_length_cm: api.shipping_length_cm,
    shipping_origin_cep: api.shipping_origin_cep,
    status: api.status,
    indexingAllowed: api.indexing_allowed !== false,
  };
}

/**
 * POST /auth/me/ads
 * Create a new ad. Requires Bearer token. BFF injects seller_id and maps to sambora.
 */
export async function createAd(payload: CreateAdPayload): Promise<ApiAdResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  // Same approach as patchAd: send payload as-is so fields (e.g. price) are not dropped
  return request<ApiAdResponse>("/auth/me/ads", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * GET /auth/me/ads
 * List current user's ads. Requires Bearer token.
 */
export async function getMyAds(): Promise<ApiAdResponse[]> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const data = await request<{ ads: ApiAdResponse[] }>("/auth/me/ads", { method: "GET" });
  return data.ads ?? [];
}

/**
 * GET /ads/:id
 * Fetch full ad by id (for ad-detail). Public, no auth required.
 */
export async function getAdById(adId: string): Promise<ApiAdResponse> {
  return request<ApiAdResponse>(`/ads/${encodeURIComponent(adId)}`, {
    method: "GET",
    skipAuth: true,
  });
}

/** Response shape for GET /feed/home and GET /ads?seller_id= (card payload with category_tree). */
export interface FeedAdCard {
  id: string;
  ad_id: string;
  title: string;
  price: number;
  category_id: string;
  category_tree: string;
  condition: string;
  seller_id: string;
  user_images?: string[];
  location?: string;
  created_at?: string;
}

/** Map feed card to Ad for list display. Uses category_tree for category when present. */
export function feedCardToAd(card: FeedAdCard): Ad {
  const tree = (card.category_tree ?? "").trim();
  const id = (card.category_id ?? "").trim();
  const humanTree = tree && (!id || tree !== id) ? tree : "";
  const category =
    humanTree || (id && !isOpaqueCatalogCategoryId(id) ? id : "") || "Outros";
  return {
    id: card.id,
    title: card.title,
    description: "",
    price: card.price,
    category,
    category_id: card.category_id || undefined,
    category_tree: humanTree || undefined,
    location: card.location ?? "",
    user_images: card.user_images ?? [],
    condition: card.condition || "Seminovo",
    seller_id: card.seller_id,
    createdAt: card.created_at ?? "",
  };
}

/** Public user profile (GET /users/:id). No auth. */
export interface PublicUserResponse {
  display_name?: string;
  profile_image_url?: string;
  member_since_year?: number;
  [key: string]: unknown;
}

/**
 * GET /users/:id
 * Public user profile for seller page. No auth required.
 */
export async function getUserPublic(userId: string): Promise<PublicUserResponse> {
  return request<PublicUserResponse>(`/users/${encodeURIComponent(userId)}`, {
    method: "GET",
    skipAuth: true,
  });
}

/**
 * GET /ads?seller_id=...
 * List active ads by seller (for seller profile). No auth required. Returns feed card shape.
 */
export async function getAdsBySeller(sellerId: string): Promise<Ad[]> {
  const path = `/ads?${new URLSearchParams({ seller_id: sellerId }).toString()}`;
  const [data, catalog] = await Promise.all([
    request<{ ads: FeedAdCard[] }>(path, {
      method: "GET",
      skipAuth: true,
    }),
    getCategories().catch(() => [] as Category[]),
  ]);
  const cards = data.ads ?? [];
  return cards.map(feedCardToAd).map((ad) => enrichAdCategoryFromCatalog(ad, catalog));
}

/**
 * GET /feed/home
 * Home feed: active ads (card payload). Public; auth is sent when present so the BFF can
 * apply "own ads last" and personalize per user. Optional recent_categories and recent_searches
 * are used for personalized ordering (Phase 2).
 */
export async function getHomeFeed(options?: {
  recent_categories?: string[];
  recent_searches?: string[];
}): Promise<Ad[]> {
  let path = "/feed/home";
  const params = new URLSearchParams();
  const cats = options?.recent_categories?.filter((c) => c?.trim());
  const searches = options?.recent_searches?.filter((s) => s?.trim());
  if (cats?.length) params.set("recent_categories", cats.join(","));
  if (searches?.length) params.set("recent_searches", searches.join(","));
  if (params.toString()) path += `?${params.toString()}`;
  const [data, catalog] = await Promise.all([
    request<{ ads: FeedAdCard[] }>(path, {
      method: "GET",
      skipAuth: false,
    }),
    getCategories().catch(() => [] as Category[]),
  ]);
  const cards = data.ads ?? [];
  return cards.map(feedCardToAd).map((ad) => enrichAdCategoryFromCatalog(ad, catalog));
}

/** Response shape for GET /ads/search (cards + optional next_cursor). */
export interface AdsSearchResponse {
  ads: FeedAdCard[];
  next_cursor?: string;
}

/**
 * GET /ads/search
 * Server-side ad search: q (title prefix), category_id or category_ids, condition, limit, cursor.
 * Prefer `category_id` only: BFF expands it to parent+descendants from product-master (do not rely on client-built category_ids).
 */
export async function getAdsSearch(params: {
  q?: string;
  /** Single category (exact match). Ignored when category_ids is set. */
  category_id?: string;
  /** Multiple categories (e.g. parent + descendants); ads matching any are returned. */
  category_ids?: string[];
  condition?: string;
  limit?: number;
  cursor?: string;
}): Promise<{ ads: Ad[]; next_cursor: string | null }> {
  const search = new URLSearchParams();
  if (params.q?.trim()) search.set("q", params.q.trim());
  if (params.category_ids?.length) {
    search.set("category_ids", params.category_ids.join(","));
  } else if (params.category_id?.trim()) {
    search.set("category_id", params.category_id.trim());
  }
  if (params.condition?.trim()) search.set("condition", params.condition.trim());
  if (params.limit != null && params.limit > 0) search.set("limit", String(params.limit));
  if (params.cursor?.trim()) search.set("cursor", params.cursor.trim());
  const path = `/ads/search?${search.toString()}`;
  const [data, catalog] = await Promise.all([
    request<AdsSearchResponse>(path, {
      method: "GET",
      skipAuth: false,
    }),
    getCategories().catch(() => [] as Category[]),
  ]);
  const cards = data.ads ?? [];
  return {
    ads: cards.map(feedCardToAd).map((ad) => enrichAdCategoryFromCatalog(ad, catalog)),
    next_cursor: data.next_cursor ?? null,
  };
}

/** One category with active listing count (sorted by ad_count desc on the server). */
export interface CategoryRankedByAds {
  category_id: string;
  ad_count: number;
}

export interface CategoriesWithAdsResponse {
  category_ids: string[];
  /** Leaf categories with ads, highest ad_count first (same ids as category_ids, ordered). */
  categories_ranked?: CategoryRankedByAds[];
}

/**
 * GET /ads/categories-with-ads
 * Returns category_ids that have at least one active ad plus optional categories_ranked for UI (e.g. footer top 5).
 * No auth. Used to filter home carousel banners.
 */
export async function getCategoriesWithAds(): Promise<CategoriesWithAdsResponse> {
  const data = await request<CategoriesWithAdsResponse>("/ads/categories-with-ads", {
    method: "GET",
    skipAuth: true,
  });
  return {
    category_ids: data.category_ids ?? [],
    categories_ranked: data.categories_ranked ?? [],
  };
}

/** Payload for PATCH /auth/me/ads/:id (full edit: title, description, price, category_id, condition, accepts_offers, user_images, status). */
export interface PatchAdPayload {
  title?: string;
  description?: string;
  price?: number;
  category_id?: string;
  condition?: "used" | "new";
  accepts_offers?: boolean;
  user_images?: string[];
  /** Optional shipping attributes for freight quote (cm, kg). */
  shipping_weight_kg?: number;
  shipping_width_cm?: number;
  shipping_height_cm?: number;
  shipping_length_cm?: number;
  /** API status: "active" | "draft" (user paused) | "paused" (system: e.g. swap awaiting second payment) | "sold" | "archived" */
  status?: string;
  /** Replaces listing_attributes on the ad when sent (validated against category schema). */
  attributes?: Record<string, unknown>;
}

/**
 * PATCH /auth/me/ads/:id
 * Update ad. Requires Bearer token; only seller can patch.
 */
export async function patchAd(adId: string, payload: PatchAdPayload): Promise<ApiAdResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ApiAdResponse>(`/auth/me/ads/${encodeURIComponent(adId)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** User-facing message for 400 listing_quality_insufficient from BFF. */
export function formatListingQualityError(err: unknown): string | null {
  const e = err as { status?: number; error?: string; issues?: Array<{ message?: string }> };
  if (e.status !== 400 || e.error !== "listing_quality_insufficient") return null;
  const parts = (e.issues ?? []).map((i) => i.message?.trim()).filter(Boolean) as string[];
  return parts.length > 0 ? parts.join(" ") : "Complete seu anúncio antes de publicar.";
}

/** GET /auth/me/ads/:id/listing-quality — score + checklist for seller's ad. */
export async function getListingQualityForAd(adId: string): Promise<ListingQualityResult> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ListingQualityResult>(
    `/auth/me/ads/${encodeURIComponent(adId)}/listing-quality`,
    { method: "GET" }
  );
}

/** POST /auth/me/listing-quality/preview — score draft without persisting. */
export async function previewListingQuality(
  payload: ListingQualityPreviewPayload
): Promise<ListingQualityResult> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ListingQualityResult>("/auth/me/listing-quality/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * DELETE /auth/me/ads/:id
 * Delete ad. Requires Bearer token; only seller can delete.
 */
export async function deleteAd(adId: string): Promise<void> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  await request<unknown>(`/auth/me/ads/${encodeURIComponent(adId)}`, {
    method: "DELETE",
  });
}

// --- Favorites (GET/POST/DELETE /auth/me/favorites) ---

/** One item in the GET /auth/me/favorites response. */
export interface FavoriteItemResponse {
  ad_id: string;
  created_at: string;
}

/** Response shape for GET /auth/me/favorites. */
export interface FavoritesResponse {
  items: FavoriteItemResponse[];
}

/**
 * GET /auth/me/favorites
 * List current user's favorites (ad_id + created_at). Requires Bearer token.
 * Same error handling as other auth endpoints (401 → refresh or session clear).
 */
export async function getFavorites(): Promise<FavoritesResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const data = await request<FavoritesResponse>("/auth/me/favorites", { method: "GET" });
  return { items: data?.items ?? [] };
}

/**
 * POST /auth/me/favorites
 * Add an ad to the current user's favorites. Requires Bearer token.
 * @throws Error with status 409 when already favorited (error: "already favorited").
 */
export async function addFavorite(adId: string): Promise<{ ad_id: string }> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<{ ad_id: string }>("/auth/me/favorites", {
    method: "POST",
    body: JSON.stringify({ ad_id: adId }),
  });
}

/**
 * DELETE /auth/me/favorites/:ad_id
 * Remove an ad from the current user's favorites. Requires Bearer token.
 * 404 from BFF is treated as success (idempotent). Other errors throw.
 */
export async function removeFavorite(adId: string): Promise<void> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  try {
    await request<unknown>(`/auth/me/favorites/${encodeURIComponent(adId)}`, {
      method: "DELETE",
    });
  } catch (err) {
    const status = (err as Error & { status?: number }).status;
    if (status === 404) return; // already removed, idempotent
    throw err;
  }
}

// --- Cart (GET / PUT /auth/me/cart) ---

/** One item in the cart API (BFF/brian-may shape). */
export interface CartItemApi {
  ad_id: string;
  quantity: number;
  offer_id?: string;
  agreed_price_cents?: number;
}

/** Response shape for GET /auth/me/cart (and PUT response). */
export interface CartResponse {
  user_id: string;
  items: CartItemApi[];
  /** When set, cart is tied to this order (resume checkout / pay existing order). */
  order_id?: string;
  updated_at: string;
}

/**
 * GET /auth/me/cart
 * Returns current cart for the authenticated user. Empty items if none. Requires Bearer token.
 */
export async function getCart(): Promise<CartResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<CartResponse>("/auth/me/cart", { method: "GET" });
}

/**
 * PUT /auth/me/cart
 * Replaces the user's cart with the given items. Optional order_id ties the cart to an order (resume checkout).
 * Requires Bearer token.
 */
export async function putCart(
  items: CartItemApi[],
  orderId?: string | null
): Promise<CartResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const body: { items: CartItemApi[]; order_id?: string } = { items: items ?? [] };
  const id = orderId?.trim();
  if (id) body.order_id = id;
  return request<CartResponse>("/auth/me/cart", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** POST /auth/me/checkout/validate — server-validated lines + subtotal; fees from platform-fees-deals (P3 one-way, P4 swap). */
export interface CheckoutValidateResponse {
  valid: boolean;
  line_items: unknown[];
  subtotal_cents: number;
  deal_type?: string;
  platform_fee_cents?: number;
  /** Buyer first PI total in cents (one-way = subtotal + shipping + platform; swap = P0 §5.1). */
  buyer_first_payment_cents?: number;
  /** P4 swap preview (when deal_type is swap). */
  swap_platform_fee_cents?: number;
  buyer_handling_cents?: number;
  second_party_handling_cents?: number;
  swap_platform_fee_base?: string;
  cash_difference_cents?: number;
  seller_product_fee_base_cents?: number;
  second_party_payment_cents?: number;
}

export interface ValidateCheckoutOptions {
  shipping_cents?: number;
  second_party_shipping_cents?: number;
}

export async function validateCheckout(
  items: CartItemApi[],
  opts?: ValidateCheckoutOptions
): Promise<CheckoutValidateResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const body: Record<string, unknown> = { items: items ?? [] };
  if (opts?.shipping_cents != null && opts.shipping_cents >= 0) {
    body.shipping_cents = opts.shipping_cents;
  }
  if (opts?.second_party_shipping_cents != null && opts.second_party_shipping_cents >= 0) {
    body.second_party_shipping_cents = opts.second_party_shipping_cents;
  }
  return request<CheckoutValidateResponse>("/auth/me/checkout/validate", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Orders (POST /auth/me/orders, GET /auth/me/orders/:order_id) ---

/** Address snapshot for create order (shipping address at time of order). */
export interface OrderAddressSnapshot {
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  /** Recipient name (buyer) for carrier/label. */
  recipient_name?: string;
  /** Recipient phone for carrier/label. */
  recipient_phone?: string;
}

/** Request body for POST /auth/me/orders (create order). */
export interface CreateOrderRequest {
  items: CartItemApi[];
  address_snapshot: OrderAddressSnapshot;
  shipping_option_id: string;
  shipping_cents: number;
  /** For swap: other party's shipping in cents (from selected option sellerAmount). Used for second PaymentIntent. */
  second_party_shipping_cents?: number;
  /** "swap" when cart is hub-based (accepted offer with trade-ins); required for second-party payment + swap_request_checkout. */
  deal_type?: string;
  /** Quoted amount per leg (hub routing). Optional; persisted on order for resume checkout. */
  leg1_quoted_cents?: number;
  leg1_1_quoted_cents?: number;
  leg2_quoted_cents?: number;
  leg2_1_quoted_cents?: number;
  /** P2: raw carrier cents for buyer shipping (one-way: quote; hub: buyer legs). */
  shipping_raw_cents?: number;
  shipping_freight_markup_cents?: number;
  /** P2: swap second party (hub: seller legs). */
  second_party_shipping_raw_cents?: number;
  second_party_shipping_freight_markup_cents?: number;
}

/** Response from POST /auth/me/orders. */
export interface CreateOrderResponse {
  order_id: string;
}

/**
 * POST /auth/me/orders
 * Creates an order (validates cart, then persists in brian-may). Returns order_id. Requires Bearer token.
 */
export async function createOrder(body: CreateOrderRequest): Promise<CreateOrderResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<CreateOrderResponse>("/auth/me/orders", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * PATCH /auth/me/orders/:order_id/contents
 * Updates order line items and totals (cart changed, re-quoted shipping). Same payload shape as create order.
 * Requires Bearer token.
 */
export async function updateOrderContents(
  orderId: string,
  payload: CreateOrderRequest
): Promise<void> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  await request<{ order_id: string }>(`/auth/me/orders/${encodeURIComponent(orderId)}/contents`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Response from POST /auth/me/orders/:order_id/payment-intent. */
export interface PaymentIntentResponse {
  client_secret: string;
  publishable_key?: string;
}

/**
 * POST /auth/me/orders/:order_id/payment-intent
 * Creates a Stripe PaymentIntent for the order; returns client_secret (and optionally publishable_key). Requires Bearer token.
 * For swap second payment, pass amount_cents so eric-clapton uses it instead of order.second_party_shipping_cents.
 * Pass second_party_shipping_option_id (hub bundle id) so post-malone creates that party's legs with the chosen carrier.
 */
export async function getPaymentIntent(
  orderId: string,
  options?: {
    amount_cents?: number;
    second_party_shipping_option_id?: string;
    second_party_shipping_cents?: number;
    second_party_shipping_raw_cents?: number;
    second_party_shipping_freight_markup_cents?: number;
  }
): Promise<PaymentIntentResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const payload: Record<string, unknown> = {};
  if (options?.amount_cents != null && options.amount_cents > 0) {
    payload.amount_cents = options.amount_cents;
  }
  if ((options?.second_party_shipping_option_id ?? "").trim() !== "") {
    payload.second_party_shipping_option_id = (options!.second_party_shipping_option_id ?? "").trim();
  }
  if (options?.second_party_shipping_cents != null && options.second_party_shipping_cents > 0) {
    payload.second_party_shipping_cents = options.second_party_shipping_cents;
  }
  if (options?.second_party_shipping_raw_cents != null) {
    payload.second_party_shipping_raw_cents = options.second_party_shipping_raw_cents;
  }
  if (options?.second_party_shipping_freight_markup_cents != null) {
    payload.second_party_shipping_freight_markup_cents = options.second_party_shipping_freight_markup_cents;
  }
  const body = Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined;
  return request<PaymentIntentResponse>(`/auth/me/orders/${encodeURIComponent(orderId)}/payment-intent`, {
    method: "POST",
    ...(body && { body }),
  });
}

/**
 * PATCH /auth/me/orders/:order_id/payment-intent
 * Updates the existing PaymentIntent amount (e.g. customer changed amount before paying). Returns new client_secret and optional publishable_key.
 * Requires Bearer token. Order must have a non-terminal PaymentIntent.
 */
export async function updatePaymentIntent(orderId: string, amountCents: number): Promise<PaymentIntentResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  if (amountCents <= 0) throw new Error("amount_cents must be positive");
  return request<PaymentIntentResponse>(`/auth/me/orders/${encodeURIComponent(orderId)}/payment-intent`, {
    method: "PATCH",
    body: JSON.stringify({ amount_cents: amountCents }),
  });
}

/** Response from GET /auth/me/orders/:order_id/payment-status (authoritative payment status from Eric-Clapton via BFF). */
export interface OrderPaymentStatusResponse {
  order_id: string;
  order_status: string;
  deal_type?: string;
  payment_intent_id?: string;
  payment_intent_id_2?: string;
  payment_intent_probes?: Array<{
    payment_intent_id: string;
    stripe_status: string;
    party?: string;
    reason?: string;
  }>;
  /** Legacy alias for Stripe PI status string */
  payment_status?: string;
  stripe_status?: string;
  is_paid: boolean;
  can_retry_payment: boolean;
  is_terminal: boolean;
  reason?: string;
}

/**
 * GET /auth/me/orders/:order_id/payment-status
 * Returns normalized, authoritative payment status for checkout return handling.
 */
export async function getOrderPaymentStatus(
  orderId: string,
  options?: { payment_intent_hint?: string }
): Promise<OrderPaymentStatusResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const hint = options?.payment_intent_hint?.trim();
  const q = hint ? `?payment_intent_hint=${encodeURIComponent(hint)}` : "";
  return request<OrderPaymentStatusResponse>(
    `/auth/me/orders/${encodeURIComponent(orderId)}/payment-status${q}`,
    {
      method: "GET",
    }
  );
}

/** POST /auth/me/orders/:order_id/finalize-payment — backend gate before showing confirmation. */
export interface FinalizePaymentResponse {
  finalized: boolean;
  already_finalized?: boolean;
  can_retry_payment?: boolean;
  is_terminal?: boolean;
  /** Swap: first leg paid, waiting for second party */
  awaiting_second_party?: boolean;
  reason?: string;
  stripe_status?: string;
}

export async function finalizePayment(
  orderId: string,
  options?: { payment_intent_hint?: string }
): Promise<FinalizePaymentResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const hint = options?.payment_intent_hint?.trim();
  const body = hint ? JSON.stringify({ payment_intent_hint: hint }) : undefined;
  return request<FinalizePaymentResponse>(`/auth/me/orders/${encodeURIComponent(orderId)}/finalize-payment`, {
    method: "POST",
    ...(body ? { body } : {}),
  });
}

/** Order summary from GET /auth/me/orders (list). Enriched with first product image/title when BFF has Sambora. */
export interface OrderSummaryApi {
  order_id: string;
  buyer_id?: string;
  status: string;
  total_cents: number;
  created_at: string;
  updated_at: string;
  /** "swap" for hub-based swap; when set, first_payer_party indicates who paid first. */
  deal_type?: string;
  first_payer_party?: string;
  ad_image?: string;
  ad_title?: string;
  /** Number of line items; when > 1, list can show "ad_title e mais x produtos". */
  items_count?: number;
}

/** Response from GET /auth/me/orders (list). */
export interface ListOrdersResponse {
  orders: OrderSummaryApi[];
}

/**
 * GET /auth/me/orders
 * Returns the list of orders for the current user (buyer). Requires Bearer token.
 */
export async function getOrdersList(): Promise<ListOrdersResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ListOrdersResponse>("/auth/me/orders", { method: "GET" });
}

/** Shipment status history entry. */
export interface ShipmentStatusHistoryEntry {
  status: string;
  at: string;
}

/** Shipment from order detail (GET /auth/me/orders/:id). Swap: leg "1" | "1.1" | "2" | "2.1" (1/2.1 = seller, 1.1/2 = buyer). */
export interface OrderShipmentApi {
  shipment_id: string;
  tracking_code: string;
  carrier: string;
  label_url?: string;
  label_storage_object?: string;
  label_status?: string;
  carrier_shipment_id?: string;
  leg?: string;
  status: string;
  status_updated_at: string;
  status_history: ShipmentStatusHistoryEntry[];
  /** DC-e (Marketplace): persisted in brian-may; BFF may hydrate `dce_dace_url` for authenticated PDF download. */
  dce_status?: string;
  dce_chave_acesso?: string;
  dce_protocolo?: string;
  dce_dace_storage_object?: string;
  dce_dace_url?: string;
  dce_last_cstat?: string;
  dce_last_xmotivo?: string;
  /** Snapshot of last cancelled DC-e (reissue); BFF may set `dce_dace_cancelled_url`. */
  dce_cancelled_chave_acesso?: string;
  dce_cancelled_protocolo?: string;
  dce_cancelled_dace_storage_object?: string;
  dce_cancelled_last_cstat?: string;
  dce_cancelled_last_xmotivo?: string;
  dce_dace_cancelled_url?: string;
}

/** Origin snapshot (seller or buyer address at order time) for hub quote. */
export interface OrderOriginSnapshot {
  cep: string;
  street?: string;
  number?: string;
  city?: string;
  state?: string;
  name?: string;
  phone?: string;
  /** Seller (or buyer origin) email stored at order time for carrier labels */
  email?: string;
}

/** Order detail including shipments (GET /auth/me/orders/:id). */
export interface OrderDetailApi {
  order_id: string;
  buyer_id?: string;
  status: string;
  line_items: unknown[];
  address_snapshot: OrderAddressSnapshot;
  /** Seller address (swap Leg 1 origin, Leg 2.1 destination). */
  origin_snapshot?: OrderOriginSnapshot | null;
  /** Buyer origin (swap Leg 1.1). */
  buyer_origin_snapshot?: OrderOriginSnapshot | null;
  shipping_option_id: string;
  shipping_cents: number;
  subtotal_cents: number;
  /** P3: platform fee on product subtotal (one-way); P4 swap: % on cash-difference or seller-product base. */
  platform_fee_cents?: number;
  /** P4 swap: handling inbound to hub (buyer leg). */
  buyer_handling_cents?: number;
  second_party_handling_cents?: number;
  swap_platform_fee_base?: string;
  swap_cash_difference_cents?: number;
  swap_seller_product_fee_base_cents?: number;
  total_cents: number;
  payment_intent_id?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
  shipments?: OrderShipmentApi[];
  deal_type?: string;
  first_payer_party?: string;
  second_party_shipping_cents?: number;
  /** Hub bundle id chosen by the party who pays second (swap); used for their two legs at label creation. */
  second_party_shipping_option_id?: string;
  /** Quoted amount per leg (hub routing). Optional; used for resume checkout display. */
  leg1_quoted_cents?: number;
  leg1_1_quoted_cents?: number;
  leg2_quoted_cents?: number;
  leg2_1_quoted_cents?: number;
  shipping_raw_cents?: number;
  shipping_freight_markup_cents?: number;
  second_party_shipping_raw_cents?: number;
  second_party_shipping_freight_markup_cents?: number;
  /** Satriani dual-confirm (swap / retirada presencial); merged by BFF from escrow_order_state. */
  escrow_confirmed_buyer?: boolean;
  escrow_confirmed_seller?: boolean;
}

/**
 * GET /auth/me/orders/:order_id
 * Returns the order for the current user (buyer). 404 if not found or not owner. Requires Bearer token.
 */
export async function getOrder(orderId: string): Promise<OrderDetailApi> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<OrderDetailApi>(`/auth/me/orders/${encodeURIComponent(orderId)}`, { method: "GET" });
}

/**
 * POST /auth/me/orders/:order_id/shipments/:shipment_id/dce/authorize — request DC-e authorization (proxied to post-malone; stub or SEFAZ).
 */
export async function postAuthorizeDCEShipment(
  orderId: string,
  shipmentId: string
): Promise<Record<string, unknown>> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<Record<string, unknown>>(
    `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/dce/authorize`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }
  );
}

/**
 * POST /auth/me/orders/:order_id/shipments/:shipment_id/dce/cancel — cancel DC-e (stub: brian-may only; production: SEFAZ event).
 */
export async function postCancelDCEShipment(
  orderId: string,
  shipmentId: string,
  justificativa: string
): Promise<Record<string, unknown>> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<Record<string, unknown>>(
    `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/dce/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ justificativa: justificativa.trim() }),
    }
  );
}

/**
 * POST /auth/me/orders/:id/shipments/:sid/request-label-generation — Correios/Jadlog async label (post-malone).
 */
export async function postRequestShipmentLabelGeneration(
  orderId: string,
  shipmentId: string
): Promise<Record<string, unknown>> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<Record<string, unknown>>(
    `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/request-label-generation`,
    { method: "POST" }
  );
}

/**
 * POST /auth/me/orders/:id/shipments/:sid/fetch-label-url — poll carrier and persist label PDF (post-malone).
 */
export async function postFetchShipmentLabelUrl(
  orderId: string,
  shipmentId: string
): Promise<Record<string, unknown>> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<Record<string, unknown>>(
    `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/fetch-label-url`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }
  );
}

/**
 * PATCH /auth/me/orders/:order_id — cancel order (body: { status: "cancelled" }).
 * Buyer: pending_payment (no charge yet) or shipping_pending (refund). Seller: shipping_pending only.
 */
export async function cancelOrder(orderId: string): Promise<{ order_id: string; status: string }> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<{ order_id: string; status: string }>(`/auth/me/orders/${encodeURIComponent(orderId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "cancelled" }),
  });
}

/**
 * POST /auth/me/orders/:order_id/confirm-receipt — confirm receipt for escrow Path A (instant release). Optional body: { party: "buyer" | "seller" } for swap.
 */
export async function confirmOrderReceipt(
  orderId: string,
  options?: { party?: "buyer" | "seller" }
): Promise<{ ok?: boolean }> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const body = options?.party != null ? JSON.stringify({ party: options.party }) : "{}";
  return request<{ ok?: boolean }>(
    `/auth/me/orders/${encodeURIComponent(orderId)}/confirm-receipt`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body }
  );
}

/**
 * Build carrier tracking URL from carrier name and tracking code (E2). Supports Correios and common BR carriers.
 */
export function buildTrackingUrl(carrier: string, trackingCode: string): string | null {
  if (!trackingCode?.trim()) return null;
  const code = trackingCode.trim();
  const c = carrier?.toLowerCase() ?? "";
  if (c.includes("correios") || c.includes("pac") || c.includes("sedex")) {
    return `https://rastreamento.correios.com.br/app/index.php?objeto=${encodeURIComponent(code)}`;
  }
  if (c.includes("jadlog")) {
    return `https://www.jadlog.com.br/jadlog/tracking?cte=${encodeURIComponent(code)}`;
  }
  if (c.includes("azul") || c.includes("azul cargo")) {
    return `https://www.azulcargo.com/rastreamento?codigo=${encodeURIComponent(code)}`;
  }
  if (c.includes("melhor") || c.includes("envio")) {
    return `https://www.melhorenvio.com.br/rastreio/${encodeURIComponent(code)}`;
  }
  return null;
}

// --- Sales (GET /auth/me/sales) — Minhas Vendas: orders where current user is seller ---

/** One sale row from GET /auth/me/sales (one order per row, not per line item — consistent with buyer list). */
export interface SaleItemApi {
  id: string;
  order_id: string;
  order_status: string;
  date: string;
  buyer_name: string;
  buyer_id?: string;
  ad_id?: string;
  ad_title: string;
  ad_image: string;
  /** Number of this seller's line items in the order; when > 1 show "ad_title e mais x produtos". */
  items_count?: number;
  /** Sum of this seller's line totals (agreed sale amount only; no shipping or platform fee). */
  total_cents: number;
  shipping_method?: string;
}

/** Response from GET /auth/me/sales. */
export interface ListSalesResponse {
  sales: SaleItemApi[];
}

/**
 * GET /auth/me/sales
 * Returns the list of sales for the current user (seller). Requires Bearer token.
 */
export async function getSalesList(): Promise<ListSalesResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ListSalesResponse>("/auth/me/sales", { method: "GET" });
}

// --- Seller wallet (Conta Digital) — balance, transactions, payout, connect links ---

/** GET /auth/me/seller/balance response (amounts in cents). */
export interface SellerBalanceResponse {
  available_cents: number;
  pending_cents: number;
  instant_available_cents?: number;
  connect_reserved_cents?: number;
  /** Phase 2 escrow: sum of unreleased escrow for this seller ("Em garantia"). */
  escrow_cents?: number;
}

/** One balance transaction from GET /auth/me/seller/balance-transactions. */
export interface SellerBalanceTransactionItem {
  id: string;
  amount_cents: number;
  net_cents: number;
  currency: string;
  type: string;
  description: string;
  status: string;
  created: number;
  available_on: number;
}

/** GET /auth/me/seller/payout-summary response (next pending payout). */
export interface SellerPayoutSummaryResponse {
  amount_cents: number;
  currency: string;
  status: string;
  arrival_date: number;
  created: number;
  payout_id?: string;
}

/** One external account from GET /auth/me/seller/payout-account (masked bank). */
export interface SellerPayoutAccountItem {
  id: string;
  last4: string;
  bank_name: string;
  status: string;
  currency: string;
  default_for_currency?: boolean;
}

/** GET /auth/me/seller/balance. Requires Bearer. 400 if no connected account (complete payout setup first). */
export async function getSellerBalance(): Promise<SellerBalanceResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<SellerBalanceResponse>("/auth/me/seller/balance", { method: "GET" });
}

/** GET /auth/me/seller/balance-transactions. Requires Bearer. Returns { data: SellerBalanceTransactionItem[] }. */
export async function getSellerBalanceTransactions(limit?: number): Promise<{ data: SellerBalanceTransactionItem[] }> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const path = limit != null ? `/auth/me/seller/balance-transactions?limit=${limit}` : "/auth/me/seller/balance-transactions";
  return request<{ data: SellerBalanceTransactionItem[] }>(path, { method: "GET" });
}

/** GET /auth/me/seller/payout-summary. Requires Bearer. Returns next pending payout or null. */
export async function getSellerPayoutSummary(): Promise<SellerPayoutSummaryResponse | null> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<SellerPayoutSummaryResponse | null>("/auth/me/seller/payout-summary", { method: "GET" });
}

/** GET /auth/me/seller/payout-account. Requires Bearer. Returns { data: SellerPayoutAccountItem[] }. */
export async function getSellerPayoutAccount(): Promise<{ data: SellerPayoutAccountItem[] }> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<{ data: SellerPayoutAccountItem[] }>("/auth/me/seller/payout-account", { method: "GET" });
}

/** GET /auth/me/seller/account-status. Requires Bearer. Returns account status, requirements, payout schedule. */
export interface SellerAccountStatusResponse {
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  currently_due?: string[];
  eventually_due?: string[];
  past_due?: string[];
  disabled_reason?: string;
  schedule_interval?: string;
  schedule_weekly_anchor?: string;
  schedule_monthly_anchor?: number;
  card_payments_status?: string;
  transfers_status?: string;
}

export async function getSellerAccountStatus(): Promise<SellerAccountStatusResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<SellerAccountStatusResponse>("/auth/me/seller/account-status", { method: "GET" });
}

/** GET /auth/me/seller/payout-failures. Requires Bearer. Returns { data: SellerPayoutFailureItem[] }. */
export interface SellerPayoutFailureItem {
  payout_id: string;
  amount_cents: number;
  currency: string;
  failure_code: string;
  failure_message: string;
  created: number;
}

export async function getSellerPayoutFailures(limit?: number): Promise<{ data: SellerPayoutFailureItem[] }> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const path = limit != null ? `/auth/me/seller/payout-failures?limit=${limit}` : "/auth/me/seller/payout-failures";
  return request<{ data: SellerPayoutFailureItem[] }>(path, { method: "GET" });
}

/** POST /auth/me/seller/connect-onboarding. Body: { return_url, refresh_url, country? }. Returns { url }. Redirect user to url. */
export interface ConnectOnboardingResponse {
  url: string;
}

export async function postSellerConnectOnboarding(body: {
  return_url: string;
  refresh_url: string;
  country?: string;
}): Promise<ConnectOnboardingResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ConnectOnboardingResponse>("/auth/me/seller/connect-onboarding", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** POST /auth/me/seller/connect-update. Body: { return_url, refresh_url }. Returns { url }. Redirect user to url for "Update payout details". */
export interface ConnectUpdateResponse {
  url: string;
}

export async function postSellerConnectUpdate(body: { return_url: string; refresh_url: string }): Promise<ConnectUpdateResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ConnectUpdateResponse>("/auth/me/seller/connect-update", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- Shipping options (GET/POST /auth/me/checkout/shipping-options) ---

/** BFF shipping option (id, name, description, estimated_days, price_cents). Hub routing adds seller_amount, buyer_amount. */
export interface ShippingOptionApi {
  id: string;
  name: string;
  description: string;
  estimated_days: number;
  price_cents: number;
  /** Present when use_hub_routing: total for seller (Leg 1 + Leg 2.1). */
  seller_amount?: number;
  /** Present when use_hub_routing: total for buyer (Leg 1.1 + Leg 2). */
  buyer_amount?: number;
  leg1_cents?: number;
  leg1_1_cents?: number;
  leg2_cents?: number;
  leg2_1_cents?: number;
  /** One-way + hub: total raw carrier cents before markup (hub = sum of four legs). */
  raw_price_cents?: number;
  /** One-way + hub: total freight markup cents (hub = sum of four legs). */
  freight_markup_cents?: number;
  /** Hub: buyer party (Leg 1.1 + Leg 2) raw cents before markup. */
  buyer_raw_price_cents?: number;
  buyer_freight_markup_cents?: number;
  /** Hub: seller party (Leg 1 + Leg 2.1) raw cents before markup. */
  seller_raw_price_cents?: number;
  seller_freight_markup_cents?: number;
  /** When hub routing: estimated days for leg Origin → Hub (Leg 1.1). */
  leg1_1_estimated_days?: number;
  /** When hub routing: estimated days for leg Hub → Destination (Leg 2). */
  leg2_estimated_days?: number;
  /** When hub routing: estimated days for Leg 1 (seller → hub). */
  leg1_estimated_days?: number;
  /** When hub routing: estimated days for Leg 2.1 (hub → seller). */
  leg2_1_estimated_days?: number;
}

/** Face-to-face same município (must match post-malone `localhandoff.OptionID`). */
export const LOCAL_HANDOFF_SHIPPING_OPTION_ID = "ezyhub:local_handoff";

/** Lista de vendas / resumos: o BFF pode devolver `shipping_option_id` em `shipping_method`. */
export function formatShippingMethodForDisplay(raw: string | undefined | null): string {
  const t = (raw ?? "").trim();
  if (!t) return "";
  if (t === LOCAL_HANDOFF_SHIPPING_OPTION_ID) return "Entrega/Retirada presencial";
  return t;
}

/** UI shipping option for checkout (price in reais, estimatedDays string, type for icon). */
export interface ShippingOptionForCheckout {
  id: string;
  name: string;
  description: string;
  estimatedDays: string;
  price: number;
  type: "standard" | "express";
  /** When hub routing: amount this party (buyer) pays. */
  buyerAmount?: number;
  /** When hub routing: amount the other party (seller) pays. */
  sellerAmount?: number;
  /** When hub routing: Leg 1 (seller → hub), in reais. */
  leg1?: number;
  /** When hub routing: this party's cost for leg Origin → Hub (Leg 1.1), in reais. */
  leg1_1?: number;
  /** When hub routing: this party's cost for leg Hub → Destination (Leg 2), in reais. */
  leg2?: number;
  /** When hub routing: Leg 2.1 (hub → seller), in reais. */
  leg2_1?: number;
  /** When hub routing: estimated days for leg Origin → Hub (Leg 1.1). */
  leg1_1EstimatedDays?: number;
  /** When hub routing: estimated days for leg Hub → Destination (Leg 2). */
  leg2EstimatedDays?: number;
  /** When hub routing: estimated days for Leg 1 (seller → hub). */
  leg1EstimatedDays?: number;
  /** When hub routing: estimated days for Leg 2.1 (hub → seller). */
  leg2_1EstimatedDays?: number;
  /** P2 audit (cents). One-way: from raw_price_cents / freight_markup_cents. */
  rawPriceCents?: number;
  freightMarkupCents?: number;
  /** Hub: buyer legs raw / markup. */
  buyerRawPriceCents?: number;
  buyerFreightMarkupCents?: number;
  /** Hub: seller legs raw / markup. */
  sellerRawPriceCents?: number;
  sellerFreightMarkupCents?: number;
}

function shippingOptionApiToUi(o: ShippingOptionApi): ShippingOptionForCheckout {
  const isLocalHandoff = o.id === LOCAL_HANDOFF_SHIPPING_OPTION_ID;
  const nameLower = o.name.toLowerCase();
  const express =
    !isLocalHandoff &&
    (nameLower.includes("sedex") || nameLower.includes("express") || nameLower.includes("expresso"));
  // When hub routing, price is only what this party (buyer) pays; do not include other participant's amount.
  const price = o.buyer_amount != null ? o.buyer_amount / 100 : o.price_cents / 100;
  const estimatedDays = isLocalHandoff
    ? "A combinar"
    : o.estimated_days === 1
      ? "1 dia útil"
      : `Até ${o.estimated_days} dias úteis`;
  const out: ShippingOptionForCheckout = {
    id: o.id,
    name: o.name,
    description: o.description,
    estimatedDays,
    price,
    type: express ? "express" : "standard",
  };
  if (o.buyer_amount != null) out.buyerAmount = o.buyer_amount / 100;
  if (o.seller_amount != null) out.sellerAmount = o.seller_amount / 100;
  if (o.leg1_cents != null) out.leg1 = o.leg1_cents / 100;
  if (o.leg1_1_cents != null) out.leg1_1 = o.leg1_1_cents / 100;
  if (o.leg2_cents != null) out.leg2 = o.leg2_cents / 100;
  if (o.leg2_1_cents != null) out.leg2_1 = o.leg2_1_cents / 100;
  if (o.leg1_1_estimated_days != null) out.leg1_1EstimatedDays = o.leg1_1_estimated_days;
  if (o.leg2_estimated_days != null) out.leg2EstimatedDays = o.leg2_estimated_days;
  if (o.leg1_estimated_days != null) out.leg1EstimatedDays = o.leg1_estimated_days;
  if (o.leg2_1_estimated_days != null) out.leg2_1EstimatedDays = o.leg2_1_estimated_days;
  if (o.raw_price_cents != null) out.rawPriceCents = o.raw_price_cents;
  if (o.freight_markup_cents != null) out.freightMarkupCents = o.freight_markup_cents;
  if (o.buyer_raw_price_cents != null) out.buyerRawPriceCents = o.buyer_raw_price_cents;
  if (o.buyer_freight_markup_cents != null) out.buyerFreightMarkupCents = o.buyer_freight_markup_cents;
  if (o.seller_raw_price_cents != null) out.sellerRawPriceCents = o.seller_raw_price_cents;
  if (o.seller_freight_markup_cents != null) out.sellerFreightMarkupCents = o.seller_freight_markup_cents;
  return out;
}

/** One shipping quote at a time per tab; parallel callers were tripping BFF rate limits. */
let shippingOptionsRequestChain: Promise<unknown> = Promise.resolve();

function runSerializedShippingOptions<T>(fn: () => Promise<T>): Promise<T> {
  const next = shippingOptionsRequestChain.then(fn, fn);
  shippingOptionsRequestChain = next.then(
    () => undefined,
    () => undefined
  );
  return next;
}

/** Optional swap (hub) params for getShippingOptions. When use_hub_routing is true, BFF/post-malone return four-leg quote with seller_amount and buyer_amount. */
export interface ShippingOptionsHubParams {
  use_hub_routing: true;
  buyer_origin_cep: string;
  seller_destination_cep: string;
  buyer_products: Array<{
    id: string;
    width: number;
    height: number;
    length: number;
    weight: number;
    insurance_value: number;
    quantity: number;
  }>;
  seller_destination_recipient_name?: string;
  seller_destination_recipient_phone?: string;
}

/**
 * POST /auth/me/checkout/shipping-options
 * Returns shipping options for the given CEP and cart. Requires Bearer token.
 * Buyer (recipient) name and phone are required; pass both in options.
 * For swap (hub routing), pass hubParams so the API returns options with seller_amount and buyer_amount.
 * Pass order_id on second-party checkout so paused listings (reserved for the order) still quote.
 */
export async function getShippingOptions(
  cep: string,
  items: Array<{ ad_id: string; quantity: number }>,
  options: {
    recipient_name: string;
    recipient_phone: string;
    hubParams?: ShippingOptionsHubParams;
    order_id?: string;
  }
): Promise<ShippingOptionForCheckout[]> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const body: Record<string, unknown> = {
    cep: cep.replace(/\D/g, "").slice(0, 8),
    items,
    destination_recipient_name: (options?.recipient_name ?? "").trim(),
    destination_recipient_phone: (options?.recipient_phone ?? "").trim(),
  };
  const oid = (options?.order_id ?? "").trim();
  if (oid) body.order_id = oid;
  if (options?.hubParams) {
    body.use_hub_routing = options.hubParams.use_hub_routing;
    body.buyer_origin_cep = options.hubParams.buyer_origin_cep.replace(/\D/g, "").slice(0, 8);
    body.seller_destination_cep = options.hubParams.seller_destination_cep.replace(/\D/g, "").slice(0, 8);
    body.buyer_products = options.hubParams.buyer_products;
    if (options.hubParams.seller_destination_recipient_name)
      body.seller_destination_recipient_name = options.hubParams.seller_destination_recipient_name;
    if (options.hubParams.seller_destination_recipient_phone)
      body.seller_destination_recipient_phone = options.hubParams.seller_destination_recipient_phone;
  }
  return runSerializedShippingOptions(async () => {
    const res = await request<{ options: ShippingOptionApi[] }>("/auth/me/checkout/shipping-options", {
      method: "POST",
      body: JSON.stringify(body),
      max429Retries: 2,
    });
    return (res.options ?? []).map(shippingOptionApiToUi);
  });
}

/** Hub display for shipping route (from BFF GET /config/hub, which reads config-platform connected-hubs). */
export interface HubDisplay {
  city: string;
  state: string;
}

let hubDisplayInFlight: Promise<HubDisplay | null> | null = null;

/**
 * GET /config/hub — returns hub city/state from config-platform (connected-hubs) for route visualization.
 * No auth required. Returns null on 404 or when hub_full_address is not configured.
 * Coalesces concurrent calls (swap checkout was spamming GET /config/hub alongside shipping POSTs).
 */
export async function getHubDisplay(): Promise<HubDisplay | null> {
  if (hubDisplayInFlight) return hubDisplayInFlight;
  hubDisplayInFlight = (async () => {
    const url = `${getBaseUrl()}/config/hub`;
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (res.status === 404) return null;
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { city?: string; state?: string } | null;
    if (!data || typeof data.city !== "string" || typeof data.state !== "string") return null;
    return { city: data.city.trim(), state: data.state.trim() };
  })();
  try {
    return await hubDisplayInFlight;
  } finally {
    hubDisplayInFlight = null;
  }
}

// --- Notifications (GET / PATCH / DELETE /auth/me/notifications, GET /auth/me/notifications/unread-count) ---

/** One item in the GET /auth/me/notifications response (Faiska shape). */
export interface NotificationItemResponse {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  deep_link?: string;
  read_at: string | null;
  created_at: string;
  metadata?: Record<string, unknown>;
}

/** Response shape for GET /auth/me/notifications. */
export interface NotificationsListResponse {
  items: NotificationItemResponse[];
  next_cursor: string;
  unread_count: number;
}

/**
 * GET /auth/me/notifications
 * List current user's notifications. Query: limit, cursor, type, unread_only. Requires Bearer token.
 */
export async function getNotifications(params?: {
  limit?: number;
  cursor?: string;
  type?: string;
  unread_only?: boolean;
}): Promise<NotificationsListResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.type) search.set("type", params.type);
  if (params?.unread_only === true) search.set("unread_only", "true");
  const qs = search.toString();
  const path = `/auth/me/notifications${qs ? `?${qs}` : ""}`;
  const data = await request<NotificationsListResponse>(path, { method: "GET" });
  return {
    items: data?.items ?? [],
    next_cursor: data?.next_cursor ?? "",
    unread_count: data?.unread_count ?? 0,
  };
}

/**
 * GET /auth/me/notifications/unread-count
 * Unread count for badge. Requires Bearer token.
 */
export async function getNotificationsUnreadCount(): Promise<number> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const data = await request<{ count: number }>("/auth/me/notifications/unread-count", { method: "GET" });
  return data?.count ?? 0;
}

/**
 * PATCH /auth/me/notifications
 * Mark as read: body { mark_read: string[] } or { mark_all_read: true }. Requires Bearer token.
 */
export async function markNotificationsRead(body: { mark_read?: string[]; mark_all_read?: boolean }): Promise<void> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  await request<unknown>("/auth/me/notifications", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /auth/me/notifications/:id
 * Remove one notification. Requires Bearer token. 404 is treated as success.
 */
export async function deleteNotification(id: string): Promise<void> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  try {
    await request<unknown>(`/auth/me/notifications/${encodeURIComponent(id)}`, { method: "DELETE" });
  } catch (err) {
    const status = (err as Error & { status?: number }).status;
    if (status === 404) return;
    throw err;
  }
}

// --- Offers (POST /auth/me/offers, GET /auth/me/offers/sent, GET /auth/me/offers/received) ---

/** Single offer from BFF/Govan (create response or list item). BFF enriches list with seller_display_name when available. */
export interface ApiOffer {
  id: string;
  ad_id: string;
  seller_id: string;
  buyer_id: string;
  type: string;
  status: string;
  amount_cents: number;
  product_ad_ids?: string[];
  message?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  seller_display_name?: string;
  seller_profile_image_url?: string;
  buyer_display_name?: string;
  /** Set in 201 response when chat (Ardanuy) find-or-create ran for this offer. */
  conversation_id?: string;
  buyer_profile_image_url?: string;
  /** Set on the counter offer (child); points to the original offer id. */
  parent_offer_id?: string;
}

/** Response for GET /auth/me/offers/sent and GET /auth/me/offers/received. */
export interface ApiOffersListResponse {
  items: ApiOffer[];
  next_cursor: string;
}

/**
 * POST /auth/me/offers — create offer (money, product, or product_money).
 * Returns the created offer on 201. Throws on 400/409/503 (caller can check err.status === 409 for "pending offer already exists").
 */
export async function createOffer(
  adId: string,
  type: "money" | "product" | "product_money",
  amountCents: number,
  message?: string,
  productAdIds?: string[]
): Promise<ApiOffer> {
  const body: Record<string, unknown> = {
    ad_id: adId,
    type,
    amount_cents: amountCents,
    message: message ?? "",
  };
  if (productAdIds && productAdIds.length > 0) {
    body.product_ad_ids = productAdIds;
  }
  return request<ApiOffer>("/auth/me/offers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * GET /auth/me/offers/sent — list offers where current user is the buyer.
 */
export async function getSentOffers(limit = 20, cursor?: string): Promise<ApiOffersListResponse> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  return request<ApiOffersListResponse>(`/auth/me/offers/sent?${params.toString()}`, { method: "GET" });
}

/**
 * GET /auth/me/offers/received — list offers where current user is the seller.
 */
export async function getReceivedOffers(limit = 20, cursor?: string): Promise<ApiOffersListResponse> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  return request<ApiOffersListResponse>(`/auth/me/offers/received?${params.toString()}`, { method: "GET" });
}

/**
 * GET /auth/me/offers/:id — fetch a single offer by id. Returns 404 if not found or not participant. Requires Bearer.
 */
export async function getOfferById(offerId: string): Promise<ApiOffer> {
  return request<ApiOffer>(`/auth/me/offers/${encodeURIComponent(offerId)}`, { method: "GET" });
}

/**
 * PATCH /auth/me/offers/:id/accept — seller accepts offer. Returns updated offer. Throws on 403/404/400.
 */
export async function acceptOffer(offerId: string): Promise<ApiOffer> {
  return request<ApiOffer>(`/auth/me/offers/${encodeURIComponent(offerId)}/accept`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/**
 * PATCH /auth/me/offers/:id/decline — seller declines offer. Returns updated offer. Throws on 403/404/400.
 */
export async function declineOffer(offerId: string): Promise<ApiOffer> {
  return request<ApiOffer>(`/auth/me/offers/${encodeURIComponent(offerId)}/decline`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/**
 * PATCH /auth/me/offers/:id/withdraw — buyer withdraws offer (pending only). Returns updated offer. Throws on 403/404/400.
 */
export async function withdrawOffer(offerId: string): Promise<ApiOffer> {
  return request<ApiOffer>(`/auth/me/offers/${encodeURIComponent(offerId)}/withdraw`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/**
 * Request body for counter offer (seller counters a pending offer).
 */
export interface CounterOfferBody {
  amount_cents: number;
  product_ad_ids?: string[];
  message?: string;
  type?: string;
}

/**
 * POST /auth/me/offers/:id/counter — seller counters a pending offer. Returns the new (counter) offer. Throws on 403/404/400.
 */
export async function counterOffer(offerId: string, body: CounterOfferBody): Promise<ApiOffer> {
  return request<ApiOffer>(`/auth/me/offers/${encodeURIComponent(offerId)}/counter`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "V";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Map BFF offer to app SentOffer shape. Uses ads to resolve ad title for targetAd and trade-in products.
 * Uses seller_display_name from BFF when present; otherwise "Vendedor".
 * When allOffers is provided and this offer was countered (status "countered"), finds the child offer in allOffers
 * and adds a "counter-offer-received" update with counterOfferAmount from the child.
 * When currentUserId is provided, sets isCurrentUserBuyer so UI can show correct banner/CTA for accepted offers.
 */
export function mapApiOfferToSentOffer(
  api: ApiOffer,
  ads: Ad[],
  allOffers?: ApiOffer[],
  currentUserId?: string | null
): {
  id: string;
  sellerName: string;
  sellerAvatar: string;
  sellerProfileImageUrl?: string;
  targetAd: Ad;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  totalOfferValue: number;
  status: "pending" | "accepted" | "rejected" | "counter-offered" | "in-cart" | "completed" | "fulfilled" | "expired" | "cancelled";
  createdAt: Date;
  expiresAt?: Date;
  updates: Array<{ type: "sent" | "counter-offer-received" | "accepted" | "rejected"; timestamp: Date; counterOfferAmount?: number }>;
  /** Set when this offer is a counter (seller's proposal); buyer can accept it. */
  parentOfferId?: string;
  /** True when current user is the buyer (show "Add to cart"); false when seller (show "Contact the buyer"). */
  isCurrentUserBuyer?: boolean;
} {
  const ad = ads.find((a) => a.id === api.ad_id);
  const targetAd: Ad = ad
    ? { ...ad, id: ad.id }
    : {
        id: api.ad_id,
        title: "Anúncio",
        description: "",
        price: 0,
        category: "",
        location: "",
      };
  const offerType: "cash-only" | "product-plus-cash" =
    api.type === "money" ? "cash-only" : "product-plus-cash";
  const status =
    api.status === "declined"
      ? "rejected"
      : api.status === "withdrawn"
        ? "cancelled"
        : api.status === "countered"
          ? "counter-offered"
          : api.status === "in_cart"
            ? "in-cart"
            : api.status;
  const cashAmount = Number(api.amount_cents) / 100;
  const sellerName = api.seller_display_name?.trim() || "Vendedor";
  const tradeInAds =
    api.product_ad_ids && api.product_ad_ids.length > 0
      ? api.product_ad_ids
          .map((id) => {
            const t = ads.find((a) => a.id === id);
            if (!t) return null;
            return {
              id: t.id,
              value: t.price ?? 0,
              title: t.title,
              image: getAdImage(t),
            };
          })
          .filter((x): x is { id: string; value: number; title: string; image: string } => x != null)
      : undefined;
  const totalOfferValue =
    tradeInAds && tradeInAds.length > 0
      ? cashAmount + tradeInAds.reduce((sum, t) => sum + t.value, 0)
      : cashAmount;

  const updates: Array<{ type: "sent" | "counter-offer-received" | "accepted" | "rejected"; timestamp: Date; counterOfferAmount?: number }> = [
    { type: "sent", timestamp: new Date(api.created_at) },
  ];
  if (api.status === "countered" && allOffers?.length) {
    const childOffer = allOffers.find((o) => o.parent_offer_id === api.id);
    if (childOffer) {
      updates.push({
        type: "counter-offer-received",
        timestamp: new Date(childOffer.created_at),
        counterOfferAmount: Number(childOffer.amount_cents) / 100,
      });
    }
  }

  const isCurrentUserBuyer = currentUserId != null && api.buyer_id === currentUserId;
  return {
    id: api.id,
    sellerName,
    sellerAvatar: initials(sellerName),
    sellerProfileImageUrl: api.seller_profile_image_url || undefined,
    targetAd,
    offerType,
    cashAmount,
    tradeInAds,
    totalOfferValue,
    status: status as "pending" | "accepted" | "rejected" | "counter-offered" | "in-cart" | "completed" | "fulfilled" | "expired" | "cancelled",
    createdAt: new Date(api.created_at),
    expiresAt: api.expires_at ? new Date(api.expires_at) : undefined,
    updates,
    parentOfferId: api.parent_offer_id || undefined,
    isCurrentUserBuyer,
  };
}

/**
 * Map BFF offer to app ReceivedOffer shape (current user can be seller or buyer for counter-offers).
 * Uses ads to resolve ad title and image for targetAd. Uses buyer_display_name from BFF when available.
 */
export function mapApiOfferToReceivedOffer(
  api: ApiOffer,
  ads: Ad[],
  currentUserId?: string | null
): {
  id: string;
  buyerName: string;
  buyerAvatar: string;
  buyerProfileImageUrl?: string;
  targetAd: Ad;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  totalOfferValue: number;
  status: "pending" | "accepted" | "rejected" | "withdrawn" | "countered";
  createdAt: Date;
  message?: string;
  isCurrentUserBuyer?: boolean;
  parentOfferId?: string;
} {
  const ad = ads.find((a) => a.id === api.ad_id);
  const targetAd: Ad = ad
    ? { ...ad, id: ad.id }
    : {
        id: api.ad_id,
        title: "Anúncio",
        description: "",
        price: 0,
        category: "",
        location: "",
      };
  const offerType: "cash-only" | "product-plus-cash" =
    api.type === "money" ? "cash-only" : "product-plus-cash";
  const status =
    api.status === "declined"
      ? "rejected"
      : api.status === "withdrawn"
        ? "withdrawn"
        : api.status === "accepted" || api.status === "fulfilled"
          ? "accepted"
          : api.status === "countered"
            ? "countered"
            : "pending";
  const cashAmount = Number(api.amount_cents) / 100;
  const buyerName = api.buyer_display_name?.trim() || "Comprador";
  const tradeInAds =
    api.product_ad_ids && api.product_ad_ids.length > 0
      ? api.product_ad_ids
          .map((id) => {
            const t = ads.find((a) => a.id === id);
            if (!t) return null;
            return {
              id: t.id,
              value: t.price ?? 0,
              title: t.title,
              image: getAdImage(t),
            };
          })
          .filter((x): x is { id: string; value: number; title: string; image: string } => x != null)
      : undefined;
  const totalOfferValue =
    tradeInAds && tradeInAds.length > 0
      ? cashAmount + tradeInAds.reduce((sum, t) => sum + t.value, 0)
      : cashAmount;
  const isCurrentUserBuyer = currentUserId != null && api.buyer_id === currentUserId;
  return {
    id: api.id,
    buyerName,
    buyerAvatar: initials(buyerName),
    buyerProfileImageUrl: api.buyer_profile_image_url || undefined,
    targetAd,
    offerType,
    cashAmount,
    tradeInAds,
    totalOfferValue,
    status: status as "pending" | "accepted" | "rejected" | "withdrawn" | "countered",
    createdAt: new Date(api.created_at),
    message: api.message || undefined,
    isCurrentUserBuyer,
    parentOfferId: api.parent_offer_id || undefined,
  };
}

// --- Conversations / Chat (GET/POST /auth/me/conversations, GET/POST .../messages) ---

/** One conversation in GET /auth/me/conversations response (BFF/Ardanuy shape). BFF enriches with seller_display_name and buyer_display_name from Bonamassa. */
export interface ApiConversationItem {
  id: string;
  ad_id: string;
  buyer_id: string;
  seller_id: string;
  participants: string[];
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  last_message_at?: string | null;
  seller_display_name?: string;
  buyer_display_name?: string;
  seller_profile_image_url?: string;
  buyer_profile_image_url?: string;
  ad_title?: string;
  seller_location?: string;
  buyer_location?: string;
  /** Unread messages for the current user (from Ardanuy); omitted when 0. */
  unread_count?: number;
}

/** Response shape for GET /auth/me/conversations. */
export interface ApiConversationsListResponse {
  items: ApiConversationItem[];
  next_cursor: string;
}

/** Single conversation from GET /auth/me/conversations/:id or POST find-or-create. BFF may enrich with seller/buyer display name, avatar, location and ad_title. */
export interface ApiConversationResponse {
  id: string;
  ad_id: string;
  buyer_id: string;
  seller_id: string;
  participants: string[];
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  last_message_at?: string | null;
  seller_display_name?: string;
  buyer_display_name?: string;
  seller_profile_image_url?: string;
  buyer_profile_image_url?: string;
  seller_location?: string;
  buyer_location?: string;
  ad_title?: string;
  unread_count?: number;
}

/** One message in GET /auth/me/conversations/:id/messages (BFF/Ardanuy shape). */
export interface ApiMessageItem {
  id: string;
  conversation_id: string;
  sender_id: string;
  type: string;
  body?: string;
  payload?: Record<string, unknown> | null;
  created_at: string;
}

/** Response shape for GET /auth/me/conversations/:id/messages. */
export interface ApiMessagesListResponse {
  items: ApiMessageItem[];
  next_cursor: string;
}

/**
 * GET /auth/me/conversations
 * List current user's conversations. Query: limit, cursor. Requires Bearer token.
 * 503 if chat service not configured.
 */
export async function getConversations(params?: {
  limit?: number;
  cursor?: string;
}): Promise<ApiConversationsListResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor ?? "");
  const qs = search.toString();
  const path = `/auth/me/conversations${qs ? `?${qs}` : ""}`;
  const data = await request<ApiConversationsListResponse>(path, { method: "GET" });
  return {
    items: data?.items ?? [],
    next_cursor: data?.next_cursor ?? "",
  };
}

/**
 * POST /auth/me/conversations
 * Find or create a conversation for the given ad (current user = buyer). Body: ad_id, optional seller_id.
 * Returns the conversation (200 existing, 201 created). Requires Bearer token.
 */
export async function findOrCreateConversation(payload: {
  ad_id: string;
  seller_id?: string;
}): Promise<ApiConversationResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ApiConversationResponse>("/auth/me/conversations", {
    method: "POST",
    body: JSON.stringify({ ad_id: payload.ad_id, seller_id: payload.seller_id ?? "" }),
  });
}

/**
 * GET /auth/me/conversations/:id
 * Get one conversation. 404 if not participant. Requires Bearer token.
 */
export async function getConversation(conversationId: string): Promise<ApiConversationResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ApiConversationResponse>(
    `/auth/me/conversations/${encodeURIComponent(conversationId)}`,
    { method: "GET" }
  );
}

/**
 * GET /auth/me/conversations/:id/messages
 * List messages (chronological). Query: limit, before (cursor). Requires Bearer token.
 */
export async function getMessages(
  conversationId: string,
  params?: { limit?: number; before?: string; /** When true, server clears this user’s unread for this thread (first page only). Omit on polling. */ markRead?: boolean }
): Promise<ApiMessagesListResponse> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.before) search.set("before", params.before);
  if (params?.markRead === true) search.set("mark_read", "true");
  const qs = search.toString();
  const path = `/auth/me/conversations/${encodeURIComponent(conversationId)}/messages${qs ? `?${qs}` : ""}`;
  const data = await request<ApiMessagesListResponse>(path, { method: "GET" });
  return {
    items: data?.items ?? [],
    next_cursor: data?.next_cursor ?? "",
  };
}

/**
 * POST /auth/me/conversations/:id/messages
 * Send a text message. Body: type (default "text"), body. Requires Bearer token.
 * Returns the created message.
 */
export async function sendMessage(
  conversationId: string,
  body: string,
  type: string = "text"
): Promise<ApiMessageItem> {
  if (!getAccessToken()) throw Object.assign(new Error("missing or invalid token"), { status: 401 });
  return request<ApiMessageItem>(
    `/auth/me/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ type, body }),
    }
  );
}

/** Map API conversation list item to UI Conversation shape (chat-list-page). Pass currentUserId to show the other party's display name and avatar when BFF enriches the list. */
export function apiConversationItemToConversation(
  item: ApiConversationItem,
  currentUserId?: string
): {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: Date;
  unreadCount: number;
  type: "support" | "seller";
  adId?: string;
  adTitle?: string;
  avatar?: string;
  location?: string;
} {
  const otherDisplayName =
    currentUserId && item.buyer_id === currentUserId
      ? item.seller_display_name
      : item.buyer_display_name;
  const name = (otherDisplayName?.trim() || item.seller_display_name?.trim() || item.buyer_display_name?.trim()) || "Vendedor";
  const avatar =
    currentUserId && item.buyer_id === currentUserId
      ? item.seller_profile_image_url
      : item.buyer_profile_image_url;
  const location =
    currentUserId && item.buyer_id === currentUserId
      ? item.seller_location?.trim()
      : item.buyer_location?.trim();
  const rawPreview = item.last_message_preview ?? "";
  const lastMessage =
    currentUserId && item.buyer_id === currentUserId && rawPreview === "Nova oferta recebida"
      ? "Nova oferta enviada"
      : rawPreview;
  return {
    id: item.id,
    name,
    lastMessage,
    timestamp: item.last_message_at ? new Date(item.last_message_at) : new Date(item.updated_at),
    unreadCount: item.unread_count ?? 0,
    type: "seller",
    adId: item.ad_id,
    adTitle: item.ad_title?.trim() || undefined,
    avatar: avatar?.trim() || undefined,
    location: location || undefined,
  };
}

/** Map API conversation (single) to same UI Conversation shape (e.g. after find-or-create). Pass currentUserId when BFF enriches so we show the other party's name, avatar, location. */
export function apiConversationToConversation(
  api: ApiConversationResponse,
  currentUserId?: string
): {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: Date;
  unreadCount: number;
  type: "support" | "seller";
  adId?: string;
  adTitle?: string;
  avatar?: string;
  location?: string;
} {
  const otherDisplayName =
    currentUserId && api.buyer_id === currentUserId
      ? api.seller_display_name
      : api.buyer_display_name;
  const name = (otherDisplayName?.trim() || api.seller_display_name?.trim() || api.buyer_display_name?.trim()) || "Vendedor";
  const avatar =
    currentUserId && api.buyer_id === currentUserId
      ? api.seller_profile_image_url
      : api.buyer_profile_image_url;
  const location =
    currentUserId && api.buyer_id === currentUserId
      ? api.seller_location?.trim()
      : api.buyer_location?.trim();
  const rawPreview = api.last_message_preview ?? "";
  const lastMessage =
    currentUserId && api.buyer_id === currentUserId && rawPreview === "Nova oferta recebida"
      ? "Nova oferta enviada"
      : rawPreview;
  return {
    id: api.id,
    name,
    lastMessage,
    timestamp: api.last_message_at ? new Date(api.last_message_at) : new Date(api.updated_at),
    unreadCount: api.unread_count ?? 0,
    type: "seller",
    adId: api.ad_id,
    adTitle: api.ad_title?.trim() || undefined,
    avatar: avatar?.trim() || undefined,
    location: location || undefined,
  };
}

/** Map API message to UI ChatMessage shape (use-app-state). currentUserId used to set sender "user" | "other". */
export function apiMessageItemToChatMessage(
  item: ApiMessageItem,
  currentUserId: string
): {
  id: string;
  conversationId: string;
  text: string;
  sender: "user" | "other";
  timestamp: Date;
  messageType?: string;
  offerData?: {
    offerId: string;
    offerType: "cash-only" | "product-plus-cash";
    totalOfferValue: number;
    targetAdTitle: string;
    targetAdId: string;
    status: "pending" | "accepted" | "rejected" | "countered";
  };
} {
  const text = item.type === "text" ? (item.body ?? "") : (item.body ?? "");
  const sender = item.sender_id === currentUserId ? ("user" as const) : ("other" as const);
  const offerData =
    item.type !== "text" && item.payload
      ? (() => {
          const rawType = (item.payload.offer_type as string) ?? "money";
          const offerType: "cash-only" | "product-plus-cash" =
            rawType === "money" ? "cash-only" : "product-plus-cash";
          const amountCents = typeof item.payload.amount_cents === "number" ? item.payload.amount_cents : 0;
          const totalFromPayload = typeof item.payload.total_offer_value === "number" ? item.payload.total_offer_value : undefined;
          const totalOfferValue = totalFromPayload != null ? totalFromPayload : amountCents / 100;
          return {
            offerId: (item.payload.offer_id as string) ?? item.id,
            offerType,
            totalOfferValue,
            targetAdTitle: (item.payload.ad_title as string) ?? "",
            targetAdId: (item.payload.ad_id as string) ?? "",
            status: ((item.payload.status as string) ?? "pending") as "pending" | "accepted" | "rejected" | "countered",
          };
        })()
      : undefined;
  return {
    id: item.id,
    conversationId: item.conversation_id,
    text,
    sender,
    timestamp: new Date(item.created_at),
    messageType: item.type !== "text" ? item.type : undefined,
    offerData,
  };
}
